import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * Prominent homepage CTA for the weekly Bula Brief digest.
 * Posts to the existing /api/newsletter endpoint (now idempotent) and
 * the same recipient list powers the APScheduler MON 09:00 Fiji cron job.
 */
export default function BulaBriefSubscribe() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const { data } = await api.post("/newsletter", { email, source: "homepage_bula_brief" });
            if (data?.already_subscribed) {
                toast.success("You're already on the list — next brief lands Monday.");
            } else {
                toast.success("Vinaka! First Bula Brief lands Monday 9 AM Fiji time.");
            }
            setDone(true);
            setEmail("");
        } catch (err) {
            toast.error("Could not subscribe. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section data-testid="bula-brief-section" className="py-20 lg:py-24 bg-secondary/40 dark:bg-card/30">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative overflow-hidden rounded-3xl bg-ocean-deep-grad text-white p-8 sm:p-12 lg:p-16 shadow-2xl">
                    {/* Decorative blob */}
                    <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-coral/20 blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-turquoise/15 blur-3xl pointer-events-none" />

                    <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/20 border border-coral/40 mb-5">
                                <Sparkles className="h-3.5 w-3.5 text-coral" />
                                <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                                    Weekly · Free · Mondays 9 AM Fiji
                                </span>
                            </div>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] mb-4">
                                The <span className="text-turquoise italic">Bula Brief</span>
                                <br />
                                in your inbox.
                            </h2>
                            <p className="font-app text-base sm:text-lg text-white/85 leading-relaxed mb-6">
                                Each Monday: the freshest 5 Joint Ventures across Fiji, one closed-deal headline, and a
                                first look at land hitting the marketplace this week. No spam. Unsubscribe anytime.
                            </p>
                            <ul className="space-y-2.5 font-app text-sm text-white/80">
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 className="h-4 w-4 text-turquoise mt-0.5 shrink-0" />
                                    <span>Top 5 Joint Ventures — vetted, scored, and ready to act on</span>
                                </li>
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 className="h-4 w-4 text-turquoise mt-0.5 shrink-0" />
                                    <span>Closed-deal headline — anonymized but real Fijian uplifts</span>
                                </li>
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 className="h-4 w-4 text-turquoise mt-0.5 shrink-0" />
                                    <span>Your personal referral link — earn on deals you introduce</span>
                                </li>
                            </ul>
                        </div>

                        <div>
                            {done ? (
                                <div data-testid="bula-brief-success" className="rounded-2xl bg-fiji-green/15 border border-fiji-green/40 p-8 text-center">
                                    <div className="h-14 w-14 rounded-full bg-fiji-green/30 grid place-items-center mx-auto mb-4">
                                        <CheckCircle2 className="h-8 w-8 text-turquoise" />
                                    </div>
                                    <div className="font-display text-2xl font-bold mb-2">You're in.</div>
                                    <p className="font-app text-sm text-white/80">
                                        Watch your inbox — first brief arrives Monday 9 AM Fiji time.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={subscribe} className="space-y-3" data-testid="bula-brief-form">
                                    <label className="block text-xs uppercase tracking-[0.2em] font-app font-semibold text-turquoise">
                                        Email address
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/50 pointer-events-none" />
                                        <Input
                                            data-testid="bula-brief-email-input"
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@email.com"
                                            className="pl-12 h-14 bg-white/10 border-white/20 text-white placeholder:text-white/45 text-base"
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="bula-brief-submit-btn"
                                        className="w-full h-14 bg-coral hover:bg-coral-deep text-white font-app text-base font-semibold"
                                    >
                                        {loading ? "Subscribing…" : (
                                            <>
                                                Get the Bula Brief <Send className="ml-2 h-4 w-4" />
                                            </>
                                        )}
                                    </Button>
                                    <p className="text-[11px] text-white/55 text-center font-app">
                                        Joining {/* placeholder count grows organically */} hundreds of Fiji-focused
                                        landowners and investors.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
